import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { Table } from '@/lib/base/table/table';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { ApiError } from '@/lib/errors';
import { getSnowflakeActionError } from '@/lib/portfolio/errors';
import { useCreateSnowflakeRequest } from '@/lib/portfolio/hooks/useCreateSnowflakeRequest';
import { useOrgSnowflakeStatuses } from '@/lib/portfolio/hooks/useOrgSnowflakeStatuses';
import type { OrgSnowflakeDisplayStatus } from '@/lib/portfolio/snowflake-types';

import { SnowflakeStatusBadge } from './SnowflakeStatusBadge';

export interface SnowflakeConnectSectionProps {
  portfolioId: number;
  orgs: ReadonlyArray<{ org_id: number; name: string }>;
}

/**
 * The action a status affords on the connect surface:
 * - `request`: offer "Request connection" (initiate, re-request after a terminal
 *   decline/revoke, or retry after a client-side error)
 * - `awaiting`: disabled "Awaiting org admin approval" — only while pending
 * - `none`: no action (connected, or a request already exists) — badge only
 */
type OrgSnowflakeAction = 'request' | 'awaiting' | 'none';

function snowflakeActionForStatus(
  status: OrgSnowflakeDisplayStatus
): OrgSnowflakeAction {
  switch (status) {
    case 'pending':
      return 'awaiting';
    case 'approved':
    case 'exists':
      return 'none';
    case 'not_requested':
    case 'declined':
    case 'revoked':
    case 'error':
      return 'request';
  }
}

const TABLE_COLUMNS = [
  { id: 'org', label: 'Organization' },
  { id: 'status', label: 'Status' },
  { id: 'action', label: 'Action' },
] as const;

interface RequestSnowflakeButtonProps {
  orgId: number;
  orgName: string;
  size: 'sm' | 'xs';
  isLoading: boolean;
  onRequest: (orgId: number, orgName: string) => void;
}

/**
 * Wraps the "Request connection" button so each row gets a stable click handler
 * (per FRONTEND_PATTERNS.md's stable-reference rule) without the parent
 * needing to recreate a per-org callback on every render.
 */
function RequestSnowflakeButton({
  orgId,
  orgName,
  size,
  isLoading,
  onRequest,
}: RequestSnowflakeButtonProps) {
  const handleClick = useCallback(() => {
    onRequest(orgId, orgName);
  }, [onRequest, orgId, orgName]);

  return (
    <Button
      size={size}
      color='primary'
      onClick={handleClick}
      isLoading={isLoading}
    >
      Request connection
    </Button>
  );
}

export function SnowflakeConnectSection({
  portfolioId,
  orgs,
}: SnowflakeConnectSectionProps) {
  const isSingleOrg = orgs.length === 1;

  const [statusByOrg, setStatusByOrg] = useState<
    Record<number, OrgSnowflakeDisplayStatus>
  >({});
  // Track pending requests per org (not a single shared flag): concurrent
  // requests across orgs must each own their loading state, so one resolving
  // never clears another that is still in flight (which would re-enable its
  // button and allow a duplicate submission).
  const [pendingOrgIds, setPendingOrgIds] = useState<ReadonlySet<number>>(
    () => new Set()
  );

  const createMutation = useCreateSnowflakeRequest(portfolioId);

  const statusesQuery = useOrgSnowflakeStatuses(portfolioId);
  const { refetch: refetchStatuses } = statusesQuery;
  const serverStatusByOrg = useMemo(() => {
    const map = new Map<number, OrgSnowflakeDisplayStatus>();
    statusesQuery.data?.orgs.forEach(o => map.set(o.org_id, o.status));
    return map;
  }, [statusesQuery.data]);

  const handleRequest = useCallback(
    async (orgId: number, orgName: string) => {
      setPendingOrgIds((prev: ReadonlySet<number>) => {
        const next = new Set(prev);
        next.add(orgId);
        return next;
      });
      try {
        await createMutation.mutateAsync(orgId);
        setStatusByOrg((prev: Record<number, OrgSnowflakeDisplayStatus>) => ({
          ...prev,
          [orgId]: 'pending',
        }));
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 400) {
          // The create endpoint returns 400 for a duplicate that is EITHER
          // pending OR approved. We can't tell which from the status code, so
          // record 'exists' ("already requested") rather than claiming a
          // specific stage — a misclaimed "Pending approval" would hide an
          // already-connected org.
          setStatusByOrg((prev: Record<number, OrgSnowflakeDisplayStatus>) => ({
            ...prev,
            [orgId]: 'exists',
          }));
        } else {
          setStatusByOrg((prev: Record<number, OrgSnowflakeDisplayStatus>) => ({
            ...prev,
            [orgId]: 'error',
          }));
          toast.error(
            `Couldn't request a Snowflake connection for ${orgName}: ${getSnowflakeActionError(err)}`
          );
        }
      } finally {
        setPendingOrgIds((prev: ReadonlySet<number>) => {
          const next = new Set(prev);
          next.delete(orgId);
          return next;
        });
      }
    },
    [createMutation]
  );

  const handleRetry = useCallback(
    () => void refetchStatuses(),
    [refetchStatuses]
  );

  if (statusesQuery.isError) {
    return (
      <section>
        <QueryErrorState
          error={statusesQuery.error}
          label='Snowflake connection status'
          onRetry={handleRetry}
        />
      </section>
    );
  }

  if (isSingleOrg) {
    const org = orgs[0];
    const status =
      serverStatusByOrg.get(org.org_id) ??
      statusByOrg[org.org_id] ??
      'not_requested';
    const action = snowflakeActionForStatus(status);

    return (
      <section>
        <h2 className='text-heading-2 text-[var(--color-text-primary)] mb-4'>
          Connect {org.name} to Snowflake
        </h2>
        <div className='flex items-center gap-4'>
          <SnowflakeStatusBadge status={status} />
          {action === 'awaiting' && (
            <Button size='sm' color='secondary' isDisabled>
              Awaiting account admin approval
            </Button>
          )}
          {action === 'request' && (
            <RequestSnowflakeButton
              orgId={org.org_id}
              orgName={org.name}
              size='sm'
              isLoading={pendingOrgIds.has(org.org_id)}
              onRequest={handleRequest}
            />
          )}
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2 className='text-heading-2 text-[var(--color-text-primary)] mb-4'>
        Snowflake data access
      </h2>
      <Table aria-label='Snowflake connection requests'>
        <Table.Header>
          {TABLE_COLUMNS.map(col => (
            <Table.Head
              key={col.id}
              id={col.id}
              label={col.label}
              isRowHeader={col.id === 'org'}
            />
          ))}
        </Table.Header>
        <Table.Body>
          {orgs.map(org => {
            const status =
              serverStatusByOrg.get(org.org_id) ??
              statusByOrg[org.org_id] ??
              'not_requested';
            const action = snowflakeActionForStatus(status);

            return (
              <Table.Row key={org.org_id}>
                <Table.Cell>
                  <span className='font-medium text-[var(--color-text-primary)]'>
                    {org.name}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <SnowflakeStatusBadge status={status} />
                </Table.Cell>
                <Table.Cell>
                  {action === 'awaiting' && (
                    <Button size='xs' color='secondary' isDisabled>
                      Awaiting account admin approval
                    </Button>
                  )}
                  {action === 'request' && (
                    <RequestSnowflakeButton
                      orgId={org.org_id}
                      orgName={org.name}
                      size='xs'
                      isLoading={pendingOrgIds.has(org.org_id)}
                      onRequest={handleRequest}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </section>
  );
}
